/**
 * Admin Context
 * Manages platform admin state including view-only impersonation
 *
 * Impersonation state is now read from AppContext (which syncs with backend)
 * This context provides convenience functions for starting/stopping impersonation
 */

import { createContext, useContext, useCallback, type ReactNode } from 'react';
import { useAuth } from './AppContext';

interface ImpersonationState {
  isImpersonating: boolean;
  accountId: number | null;
  accountName: string | null;
}

interface AdminContextValue {
  impersonation: ImpersonationState;
  startImpersonation: (accountId: number, accountName: string) => void;
  endImpersonation: () => void;
}

const AdminContext = createContext<AdminContextValue | undefined>(undefined);

export function AdminProvider({ children }: { children: ReactNode }) {
  const { impersonating, setImpersonating } = useAuth();

  const startImpersonation = useCallback((_accountId: number, accountName: string) => {
    // Optimistically update state - the page will reload and sync from backend
    setImpersonating({ viewOnly: true, accountName });
  }, [setImpersonating]);

  const endImpersonation = useCallback(async () => {
    try {
      await fetch('/api/admin/impersonate/stop', {
        method: 'POST',
        credentials: 'include',
      });
      setImpersonating(null);
      // Reload to refresh all data for admin's own account
      window.location.reload();
    } catch (err) {
      console.error('Failed to stop impersonation:', err);
    }
  }, [setImpersonating]);

  return (
    <AdminContext.Provider
      value={{
        impersonation: {
          isImpersonating: !!impersonating,
          accountId: null, // Not needed - backend is source of truth
          accountName: impersonating?.accountName || null,
        },
        startImpersonation,
        endImpersonation,
      }}
    >
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin() {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
}
